/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import React, { useEffect, useRef, useState } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';

interface TimeInputProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
}

const isValidTime = (val: string): boolean => {
  if (!/^\d{2}:\d{2}$/.test(val)) return false;
  const [h, m] = val.split(':').map(Number);
  return h >= 0 && h <= 23 && m >= 0 && m <= 59;
};

const TimeInput: React.FC<TimeInputProps> = ({
  value,
  onChange,
  options,
  placeholder = 'HH:MM',
}) => {
  const [inputValue, setInputValue] = useState(value);
  const initialValue = useRef(value);

  useEffect(() => {
    setInputValue(value);
    initialValue.current = value;
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    if (isValidTime(val)) {
      initialValue.current = val;
      onChange(val);
    }
  };

  const handleBlur = () => {
    if (!isValidTime(inputValue)) {
      setInputValue(initialValue.current);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.metaKey || e.ctrlKey) return;
    if (
      !/^[0-9:]$/.test(e.key) &&
      ![
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
        'Delete',
        'Tab',
        'Backspace',
        'Enter',
      ].includes(e.key)
    ) {
      e.preventDefault();
    }
  };

  const handleOptionClick = (option: string) => {
    setInputValue(option);
    initialValue.current = option;
    onChange(option);
  };

  const selectValue = options.includes(inputValue) ? inputValue : undefined;

  return (
    <Select value={selectValue} onValueChange={handleOptionClick}>
      <SelectTrigger className="[&>svg]:text-form-input-text/70 h-11 w-[107px] p-3 text-form-input-text font-normal focus:ring-primary focus:ring-offset-0">
        <input
          type="text"
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          maxLength={5}
          placeholder={placeholder}
          onFocus={(e) => {
            if (window.innerWidth > 1024) e.target.select();
          }}
          className="w-full border-none bg-transparent p-0 text-center text-form-input-text outline-none"
        />
      </SelectTrigger>
      <SelectContent className="text-body-main max-h-60 w-[107px] overflow-auto rounded bg-card-00 shadow-lg">
        {!options.includes(inputValue) && isValidTime(inputValue) && (
          <SelectItem
            key={inputValue}
            value={inputValue}
            className="bg-card-02 py-1 text-center text-[13px] font-semibold"
          >
            {inputValue}
          </SelectItem>
        )}
        {options.map((t) => (
          <SelectItem
            key={t}
            value={t}
            className="py-1 text-center text-[13px]"
          >
            {t}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

TimeInput.displayName = 'TimeInput';

export { TimeInput };
